import React, { useEffect, useMemo, useState } from 'react';
import { List, Pagination } from 'antd';
import useQueryApiClient from 'utils/useQueryApiClient';
import { StyledMobileList } from './style';
import { Spinner } from 'ui';
import { MobileListCard, MobileListField } from 'ui/Card/MobileListCard';
import { Pagination as PaginationType } from 'types/Pagination';
import { useIntl } from 'react-intl';
import { Empty } from 'antd';
import useScroll from '../../../../utils/useScroll';

interface MobileListProps {
  url?: string;
  coverFields?: MobileListField[];
  bodyFields: MobileListField[];
  rowKey?: string;
  filter?: object;
  paginnationStatus?: React.Dispatch<React.SetStateAction<PaginationType>>;
  page_size?: number;
  reload?: number;
  dataSource?: any[];
  footerFields?: MobileListField[];
  options?: {};
  className?: string;
  bodyTripleSectionFields?: MobileListField[];
  onItemClick?: (record: any) => void;
  rowClassName?: (record: any) => string;
  emptyText?: React.ReactNode;
}

const MobileList = ({
  url,
  coverFields,
  bodyFields,
  rowKey = 'id',
  filter,
  page_size = 25,
  paginnationStatus,
  reload,
  dataSource,
  footerFields,
  options,
  className,
  bodyTripleSectionFields,
  onItemClick,
  rowClassName,
  emptyText,
}: MobileListProps) => {
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const intl = useIntl();
  const { scrollToTop } = useScroll();

  const { page_size: filterPageSize, ...requestFilter } = (filter ?? {}) as {
    page_size?: number;
    [key: string]: unknown;
  };

  const derivedPageSize = useMemo(() => {
    return Number.isInteger(filterPageSize)
      ? Number(filterPageSize)
      : page_size;
  }, [filterPageSize, page_size]);

  const { isLoading: isDataFetching, refetch } = useQueryApiClient({
    request: {
      url: url ?? '',
      data: {
        page: currentPage,
        page_size: derivedPageSize,
        filter: requestFilter,
        options: options,
      },
      disableOnMount: true,
    },
    onSuccess: (response: any) => {
      setData(response.data ?? response);
      setTotal(response.total || 0);

      if (currentPage !== 1 && response.data.length === 0) {
        setCurrentPage((old) => (old ? old - 1 : 1));
      }

      paginnationStatus?.({
        page: currentPage,
        page_size: derivedPageSize,
        total: response.total,
      });
    },
  });

  useEffect(() => {
    if (url && !dataSource) {
      refetch();
    }
  }, [currentPage, filter, derivedPageSize, reload]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    scrollToTop();
  };

  const finalData = dataSource ?? data;

  return (
    <StyledMobileList className={className}>
      <Spinner spinning={isDataFetching && !dataSource}>
        <List
          locale={{
            emptyText: (
              <Empty
                description={
                  emptyText ??
                  intl.formatMessage({ id: 'general.select_no_data' })
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          dataSource={finalData}
          rowKey={rowKey}
          renderItem={(record) => (
            <MobileListCard
              record={record}
              coverFields={coverFields}
              bodyFields={bodyFields}
              footerFields={footerFields}
              bodyTripleSectionFields={bodyTripleSectionFields}
              onClick={onItemClick ? () => onItemClick(record) : undefined}
              className={rowClassName?.(record)}
            />
          )}
        />

        {!dataSource && total > derivedPageSize && (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <Pagination
              current={currentPage}
              total={total}
              pageSize={derivedPageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              responsive={true}
            />
          </div>
        )}
      </Spinner>
    </StyledMobileList>
  );
};

export default MobileList;
